import { IsArray, IsBoolean, IsEnum, IsNumberString, IsOptional, IsString, ValidateNested } from 'class-validator';
import { Type } from 'class-transformer';
import { LayerDto, LayerType } from './layer.dto';
import { String } from '../../../_core/utils/utils.string';
import {
	hasValidSubjectPosition,
	validateSubjectPositionBounds,
	convertSubjectPositionToFloats
} from '../../../asset/utils/subject-position.utils';
import { CacheBusterUtils } from '../../../_core/utils/utils.cache-buster';
import { NumberUtils } from '../../../_core/utils/utils.number';
import { reduceAspectRatio } from '../../../asset/utils/aspect-ratio.utils';

export enum ResizeMode {
	AUTO = 'auto',
	SCALE = 'scale',
	FIT = 'fit',
	FILL = 'fill',
	LIMIT = 'limit',
	CROP = 'crop',
	THUMB = 'thumb',
	MFIT = 'mfit',
	PAD = 'pad',
	LPAD = 'lpad',
	MPAD = 'mpad',
	LFILL = 'lfill',
	FILL_PAD = 'fill_pad'
}

export enum Gravity {
	AUTO = 'auto',
	NORTH_EAST = 'north_east',
	NORTH = 'north',
	NORTH_WEST = 'north_west',
	WEST = 'west',
	SOUTH_WEST = 'south_west',
	SOUTH = 'south',
	SOUTH_EAST = 'south_east',
	EAST = 'east',
	CENTER = 'center',
	XY_CENTER = 'xy_center',
	ENHANCED_AUTO = 'attention'
}

export enum ZoomGravity {
	NORTH_EAST = 'north_east',
	NORTH = 'north',
	NORTH_WEST = 'north_west',
	WEST = 'west',
	SOUTH_WEST = 'south_west',
	SOUTH = 'south',
	SOUTH_EAST = 'south_east',
	EAST = 'east',
	CENTER = 'center'
}

export class LayerOptimizationResizeDto {
	@IsOptional()
	@IsEnum(ResizeMode)
	mode: ResizeMode;

	@IsOptional()
	@IsString()
	width: string;

	@IsOptional()
	@IsString()
	height: string;

	@IsOptional()
	@IsEnum(Gravity)
	gravity: Gravity;

	@IsOptional()
	@IsNumberString()
	x: string;

	@IsOptional()
	@IsNumberString()
	y: string;

	@IsOptional()
	@IsNumberString()
	zoom: string;

	@IsOptional()
	@IsString()
	zoomGravity: ZoomGravity;

	@IsOptional()
	@IsString()
	maxResolution?: string;
}

export class LayerOptimizationTargetCropDto {
	@IsOptional()
	@IsBoolean()
	autoTargetCropEnabled?: boolean; // Auto mode uses focal areas, manual uses tx/ty/tw/th

	@IsOptional()
	tx: string; // Focal Area Box X

	@IsOptional()
	ty: string; // Focal Area Box Y

	@IsOptional()
	tw: string; // Focal Area Box Width

	@IsOptional()
	th: string; // Focal Area Box Height

	@IsOptional()
	@IsString()
	margin: string; // Focal Area Box Margin

	@IsOptional()
	@IsString()
	width: string;

	@IsOptional()
	@IsString()
	height: string;

	@IsOptional()
	@IsString()
	cropMode: 'cover' | 'contain';

	@IsOptional()
	@IsString()
	aspectPriority?: 'crop' | 'target'; // How to prioritize aspect ratio vs. target area (default: 'target')
}

export class LayerOptimizationETrimDto {
	@IsOptional()
	@IsBoolean()
	enabled: boolean;

	@IsOptional()
	tolerance: string | number;

	/**
	 * Cloudinary only. When true, the trim is emitted as its own step BEFORE the resize, so it runs
	 * against the original image and the resize lands at the correct final dimensions.
	 *
	 * Defaults to false, which keeps the legacy order where the trim shares the resize step and
	 * Cloudinary trims the already-scaled canvas. Many templates were authored against that legacy
	 * output and rely on it, so the correct order is opt-in rather than the default. Has no effect on
	 * Tasker, which always emits the trim as its own step (it trims but never resizes otherwise).
	 */
	@IsOptional()
	@IsBoolean()
	trimFirst?: boolean;
}

export class LayerOptimizationSubjectPositionDto {
	@IsOptional()
	sx?: string | number; // Subject position X coordinate (percentage: 0-100)

	@IsOptional()
	sy?: string | number; // Subject position Y coordinate (percentage: 0-100)

	@IsOptional()
	sw?: string | number; // Subject position width (percentage: 0-100)

	@IsOptional()
	sh?: string | number; // Subject position height (percentage: 0-100)
}

export class LayerOptimizationsDto {
	@IsOptional()
	@IsString()
	provider?: 'cloudinary' | 'tasker';

	@IsOptional()
	@IsString()
	quality?: string;

	@IsOptional()
	videoTrimming?: {
		startOffset?: number;
		endOffset?: number;
		duration?: number;
	};

	@IsOptional()
	@IsString()
	baseline?: string; // https://cloudinary.com/documentation/transformation_reference#bl_baseline

	@IsOptional()
	@ValidateNested()
	@Type(() => LayerOptimizationResizeDto)
	resize?: LayerOptimizationResizeDto;

	@IsOptional()
	@ValidateNested()
	@Type(() => LayerOptimizationTargetCropDto)
	targetCrop?: LayerOptimizationTargetCropDto;

	@IsOptional()
	@IsArray()
	@IsString({ each: true })
	targetFilterTags?: string[];

	@IsOptional()
	@IsString()
	advanced?: string;

	@IsOptional()
	@IsBoolean()
	advancedOptimizationsEnabled?: boolean;

	@IsOptional()
	@ValidateNested()
	@Type(() => LayerOptimizationETrimDto)
	eTrim: LayerOptimizationETrimDto;

	@IsOptional()
	@ValidateNested()
	@Type(() => LayerOptimizationSubjectPositionDto)
	subjectPosition?: LayerOptimizationSubjectPositionDto;

	/**
	 * When true, disables the `we_true` (width enforcement) parameter for Tasker URLs.
	 * By default, we_true is added to prevent Tasker from upscaling images.
	 * Set this to true when you explicitly want to allow upscaling (e.g., file library preview).
	 */
	@IsOptional()
	@IsBoolean()
	disableWidthEnforcement?: boolean;

	/**
	 * Builds a c_subpos transformation string for combining subject position, target crop, and resize.
	 * This method constructs the transformation URL parameters for Taskr's c_subpos operation.
	 *
	 * @param subjectPosition - Subject position bounds (sx, sy, sw, sh) as percentages (0-100)
	 * @param targetCrop - Target crop configuration including tx/ty/tw/th and settings
	 * @param resizeConfig - Resize configuration with width/height
	 * @param isAutoMode - Whether to use automatic focal area detection (target_auto)
	 * @param hasManualValues - Whether manual tx/ty/tw/th coordinates are provided
	 * @param targetFilterSegment - Filter tags string for focal area selection
	 * @returns The c_subpos transformation string
	 */
	private static buildSubposTransformation(
		subjectPosition: LayerOptimizationSubjectPositionDto,
		targetCrop: LayerOptimizationTargetCropDto,
		resizeConfig: LayerOptimizationResizeDto,
		isAutoMode: boolean,
		hasManualValues: boolean,
		targetFilterSegment: string
	): string {
		// Convert subject position from 0-100 percentages to 0.0-1.0 floats
		const subjectFloats = convertSubjectPositionToFloats(subjectPosition);

		// Start building the c_subpos transformation
		let transformation = 'c_subpos';

		// Add subject position parameters (always required for c_subpos)
		transformation += `,sx_${subjectFloats.sx},sy_${subjectFloats.sy},sw_${subjectFloats.sw},sh_${subjectFloats.sh}`;

		// Handle target coordinates based on mode
		if (isAutoMode && !hasManualValues) {
			// Automatic mode: use target_auto placeholder for API to inject focal area
			transformation += `,target_auto${targetFilterSegment}`;
		} else if (hasManualValues) {
			// Manual mode: use provided tx/ty/tw/th coordinates
			transformation +=
				`,tx_${LayerOptimizationsDto.asCropToken(targetCrop.tx)},ty_${LayerOptimizationsDto.asCropToken(targetCrop.ty)}` +
				`,tw_${LayerOptimizationsDto.asCropToken(targetCrop.tw)},th_${LayerOptimizationsDto.asCropToken(targetCrop.th)}`;
		}

		// Add width and height (required for c_subpos)
		let width = Math.ceil(Number(resizeConfig.width || 0));
		let height = Math.ceil(Number(resizeConfig.height || 0));

		// Apply max resolution if specified
		if (resizeConfig.maxResolution) {
			let maxResolution = Number(resizeConfig.maxResolution);
			let scale = Math.min(maxResolution / width, maxResolution / height);
			if (scale < 1) {
				width = Math.ceil(width * scale);
				height = Math.ceil(height * scale);
			}
		}

		if (width) {
			transformation += `,w_${width}`;
		} else {
			transformation += `,w_$element.width`;
		}

		if (height) {
			transformation += `,h_${height}`;
		} else {
			transformation += `,h_$element.height`;
		}

		// Note: ar_ (aspect ratio) is NOT needed when w and h are provided
		// Taskr will use the provided w/h dimensions directly

		// Add aspect mode (am_contain or am_cover)
		// if (targetCrop.cropMode === 'contain') {
		transformation += ',am_contain';
		// } else {
		// transformation += ',am_cover';
		// }

		// Add resize flag (defaults to true)
		transformation += ',r_true';

		return transformation;
	}

	/*
	 * Check if targetCrop has actual configuration (not just an empty object)
	 * Only process if autoTargetCropEnabled is explicitly true OR manual values exist
	 */
	/**
	 * The four crop fractions read back out of an asset URL this class produced, or null when it
	 * carries none. The inverse of the c_target segment `optimizeAsset` writes, and it lives here
	 * so the parser and the serializer move together: it used to sit in a web component two apps
	 * away, where a change to token order or format would have degraded silently.
	 *
	 * Keyed off the tokens themselves so both transforms that carry them are covered - `c_target`
	 * and the `c_subpos` variant - with a leading boundary so `tx_` cannot match inside another
	 * token. The first match wins, which is the outer transform: it precedes the encoded source
	 * URL, so a source that happens to contain the same tokens cannot shadow it.
	 *
	 * This answers "what does this URL ask for", which is not the same question as "what is
	 * stored": callers use it to find out what the canvas actually painted, which no state
	 * resolver can answer because the render is asynchronous.
	 */
	public static readTargetCropFractionsFromUrl(
		url: string | null | undefined
	): { tx: number; ty: number; tw: number; th: number } | null {
		if (!url) {
			return null;
		}

		// The buster comes off first. Reading named tokens would step over it anyway, but a buster
		// is definitionally not part of a crop and the parse should not have to rely on that.
		const path = CacheBusterUtils.removeCacheBusterFromUrl(url) ?? url;
		const read = (key: string): number => {
			const match = new RegExp(`(?:^|[^A-Za-z0-9])${key}_(-?\\d*\\.?\\d+)`).exec(path);
			return match ? parseFloat(match[1]) : NaN;
		};

		const fractions = { tx: read('tx'), ty: read('ty'), tw: read('tw'), th: read('th') };
		return [fractions.tx, fractions.ty, fractions.tw, fractions.th].every(Number.isFinite) ? fractions : null;
	}

	public static reduceAspectRatio(width: unknown, height: unknown): string | null {
		return reduceAspectRatio(width, height);
	}

	/**
	 * The single place a crop is turned into an optimization write.
	 *
	 * Every writer previously re-derived these conventions by hand, and each one got a different
	 * subset of them right: three unit conventions reached tx/ty/tw/th, two copy-paste fraction
	 * converters disagreed on their guards, and three separate policies decided aspectPriority.
	 * Six defects in that seam traced back to a writer not knowing a rule that lived only in prose.
	 * The rules now live here, once:
	 *
	 * - tx/ty/tw/th are FRACTIONS of the source image, clamped to 0..1 and emitted as float
	 *   strings. A caller holding pixels converts before calling; this refuses to guess, because
	 *   a pixel value silently asks the proxy for a region orders of magnitude too large.
	 * - autoTargetCropEnabled is false: a rect that was drawn is by definition not automatic.
	 * - aspectPriority is EXPLICIT or absent, never inherited. 'crop' gives the ratio priority
	 *   over the requested area, so the proxy re-frames away from the rect that was drawn; once
	 *   written it used to survive forever through the spread.
	 * - cropMode is explicit, else inherited, else 'contain'.
	 * - frame is the crop viewport in pixels, stored as targetCrop.width/height. The URL carries
	 *   the ar_$element.ar placeholder; the renderer resolves it from the rendered frame and falls
	 *   back to these when an axis is auto or measures 0 while the bitmap loads, so a fluid layer
	 *   keeps its crop. A frame that cannot be measured leaves both keys off. Rounded to integers.
	 * - provider resolves to tasker: c_target has no Cloudinary equivalent.
	 * - resize keeps whatever the base had, defaulting the mode only when absent. resize is not
	 *   crop state and must not be cleared, or the layer loses its resolution cap.
	 * - previewOptimizations, when the layer has any, gets its competing keys neutralised with
	 *   undefined and NOT null: conversion merges preview over main with lodash merge, which skips
	 *   undefined but assigns null, so nulls here overwrite the crop that was just written. resize
	 *   is left alone there too, for the same reason as above.
	 */
	/**
	 * A crop extent as the asset service needs to read it.
	 *
	 * Extents are fractions of the source, but the service tells a fraction from a pixel count by
	 * spelling: `tw_1` is read as ONE PIXEL, not the whole width. Cropping on a single axis - full
	 * width, trimmed vertically - produces exactly that, and the service then answers 500, or 200
	 * carrying a body nothing downstream can tell from an image. `tw_1.0` is the same crop, read
	 * correctly.
	 *
	 * Only the bare `1` (and `0`) are ambiguous; a pixel-valued extent means the same either
	 * spelling, verified against the service, so this is safe to apply to every extent.
	 */
	public static asCropToken(value: any): any {
		const extent = Number(value);

		// Anything above 1 is unambiguously a pixel count and is left byte-identical; anything
		// unreadable (a merge tag, an empty) is resolved later and must pass through untouched.
		if (String.isEmpty(value) || !Number.isFinite(extent) || extent < 0 || extent > 1) {
			return value;
		}

		return NumberUtils.ensureFloatString(extent);
	}

	public static buildCropWrite(input: {
		fractions: { tx: number; ty: number; tw: number; th: number };
		frame: { width: number; height: number };
		base?: LayerOptimizationsDto;
		preview?: LayerOptimizationsDto;
		cropMode?: string;
		aspectPriority?: 'crop' | 'target';
	}): { optimizations: LayerOptimizationsDto; previewOptimizations?: LayerOptimizationsDto } {
		const { fractions, base, preview } = input;

		const clamp01 = (value: number): number => Math.min(1, Math.max(0, Number(value) || 0));
		const asFraction = (value: number): string => NumberUtils.ensureFloatString(clamp01(value));
		const tx = clamp01(fractions.tx);
		const ty = clamp01(fractions.ty);
		const tw = Math.min(clamp01(fractions.tw), 1 - tx);
		const th = Math.min(clamp01(fractions.th), 1 - ty);

		// Subpixel floats reach gcd() in the emitter, where they produce garbage or fail to
		// terminate. A frame that cannot be measured leaves both keys off rather than inventing a
		// ratio: the URL then keeps today's behaviour instead of asking for the wrong region.
		const frameWidth = Math.round(Number(input.frame?.width) || 0);
		const frameHeight = Math.round(Number(input.frame?.height) || 0);
		const hasFrame = frameWidth > 0 && frameHeight > 0;

		const targetCrop: LayerOptimizationTargetCropDto = {
			...base?.targetCrop,
			tx: asFraction(tx),
			ty: asFraction(ty),
			tw: asFraction(tw),
			th: asFraction(th),
			// An unmeasurable new frame must not inherit the previous crop's ratio.
			// That ratio describes different geometry and would render a valid crop into
			// the wrong viewport while looking superficially well-formed.
			...(hasFrame ? { width: `${frameWidth}`, height: `${frameHeight}` } : { width: undefined, height: undefined }),
			autoTargetCropEnabled: false,
			cropMode: input.cropMode || base?.targetCrop?.cropMode || 'contain',
			aspectPriority: input.aspectPriority
		} as LayerOptimizationTargetCropDto;

		const optimizations: LayerOptimizationsDto = {
			...base,
			// Manual target crops use Tasker's c_target syntax. Keeping a pre-existing
			// Cloudinary provider here would send that syntax to the wrong backend and
			// silently render the uncropped source.
			provider: 'tasker',
			resize: {
				...base?.resize,
				mode: base?.resize?.mode || ResizeMode.FILL
			},
			targetCrop
		} as LayerOptimizationsDto;

		if (!preview) {
			return { optimizations };
		}

		return {
			optimizations,
			previewOptimizations: {
				...preview,
				provider: undefined,
				targetCrop: undefined
			} as LayerOptimizationsDto
		};
	}

	public static hasTargetCropConfig(targetCrop?: LayerOptimizationTargetCropDto): boolean {
		return !!(
			targetCrop &&
			(targetCrop.autoTargetCropEnabled === true ||
				!String.isEmpty(targetCrop.tx) ||
				!String.isEmpty(targetCrop.ty) ||
				!String.isEmpty(targetCrop.tw) ||
				!String.isEmpty(targetCrop.th))
		);
	}

	public static optimizeLayer(layer: LayerDto, bucketId?: string, taskerEngineUrl?: string, apiUrl?: string, seed?: number) {
		let assetPath = layer[layer.type]?.assetPath || null;

		return {
			...layer,
			[layer.type]: {
				...layer[layer.type],
				assetPath: this.optimizeAsset(assetPath, layer.type, layer.optimizations, bucketId, taskerEngineUrl, apiUrl, seed)
			}
		};
	}

	/**
	 * Detects whether an assetPath is already a CCE-applied transformation URL.
	 *
	 * convertToCreativeUnit can run twice on the same unit (server-side via the
	 * /cart-items/preview endpoint, then client-side in ad-render-container).
	 * Without this guard the second pass re-wraps an already-transformed URL,
	 * and for Cloudinary `image/fetch/` URLs the splice path below would emit
	 * a relative URL (uploadIndex = -1 → splice at index 0 → leading slash),
	 * which the browser then resolves against the page origin.
	 */
	public static isAlreadyOptimized(assetPath: string): boolean {
		if (!assetPath || typeof assetPath !== 'string') return false;
		// Cloudinary fetch URL wrapping an inner http(s) URL
		if (/\/(image|video)\/fetch\/[^?#]+\/https?:\/\//.test(assetPath)) return true;
		// Tasker / API process URL: <host>/asset/<transforms>/path/<inner-http(s)-url>
		// (also matches /asset/process/<transforms>/path/<inner>)
		if (/\/asset\/(?:process\/)?[^?#]+\/path\/https?:\/\//.test(assetPath)) return true;
		return false;
	}

	/**
	 * Whether the layer carries a fully drawn manual crop.
	 *
	 * Restricted to MANUAL crops, matching the tx/ty/tw/th test the emitter itself applies. Auto
	 * mode emits target_auto for the asset proxy to resolve, and that path has its own Cloudinary
	 * handling which strips these tokens before falling back; treating it as drawn here would
	 * change behaviour this guard has no evidence about.
	 */
	public static hasDrawnTargetCrop(optimizations?: LayerOptimizationsDto): boolean {
		return (
			!String.isEmpty(optimizations?.targetCrop?.tx) &&
			!String.isEmpty(optimizations?.targetCrop?.ty) &&
			!String.isEmpty(optimizations?.targetCrop?.tw) &&
			!String.isEmpty(optimizations?.targetCrop?.th)
		);
	}

	/** Whether the layer carries any target crop configuration at all. */
	public static hasAnyTargetCrop(optimizations?: LayerOptimizationsDto): boolean {
		return !!optimizations?.targetCrop && Object.keys(optimizations.targetCrop).length > 0;
	}

	/**
	 * The provider that will actually serve this layer, after the coercions below. Callers outside
	 * this class need it to reason about which engine a URL will hit.
	 *
	 * A DRAWN crop forces tasker because c_target is not a Cloudinary transform, and Cloudinary
	 * validates strictly - it answers 400 with an empty body, so the layer renders as a broken
	 * image. Pinning the provider is a supported authoring choice with its own control in the
	 * layer's Advanced Optimizations, and real templates use it on exactly the image layers people
	 * crop, so the pairing is ordinary data rather than a mistake to refuse.
	 *
	 * Coerced at this one point rather than at the writers because every crop passes through here:
	 * all three writers preserve whatever provider the layer already carried, mappings can merge one
	 * in afterwards, and layers stored before this guard has to render too. Forced rather than
	 * dropped - dropping the crop serves the whole uncropped image, which looks plausible and
	 * reports nothing.
	 */
	public static resolveProvider(
		optimizations: LayerOptimizationsDto | undefined,
		type: LayerType
	): NonNullable<LayerOptimizationsDto['provider']> {
		// Default to cloudinary unless target crop is enabled, which requires tasker
		let provider: NonNullable<LayerOptimizationsDto['provider']> =
			optimizations?.provider || (LayerOptimizationsDto.hasAnyTargetCrop(optimizations) ? 'tasker' : 'cloudinary');

		// Enhanced Auto (g_attention) requires tasker regardless of provider setting
		if (optimizations?.resize?.gravity === 'attention') {
			provider = 'tasker';
		}

		if (type === LayerType.IMAGE && provider === 'cloudinary' && LayerOptimizationsDto.hasDrawnTargetCrop(optimizations)) {
			provider = 'tasker';
		}

		return provider;
	}

	public static optimizeAsset(
		assetPath: string,
		type: LayerType,
		optimizations: LayerOptimizationsDto,
		bucketId?: string,
		taskerEngineUrl?: string,
		apiUrl?: string,
		seed?: number
	) {
		// Idempotency guard: never re-wrap a URL that we already transformed.
		if (this.isAlreadyOptimized(assetPath)) {
			return assetPath;
		}

		let resizeConfig = optimizations?.resize;
		let qualityOptimization = optimizations?.quality ? `${optimizations?.quality}` : '';
		let eTrimOptimization = optimizations?.eTrim;
		let zoomOptimization = '';
		// Tasker always emits the trim as its own step before the resize: in the same step as
		// w_/h_/c_, Tasker trimmed but never resized. Cloudinary keeps the trim in the resize step
		// by default (legacy behaviour many templates rely on) and only moves it to its own step
		// before the resize when eTrim.trimFirst is set — see the Cloudinary branch below.
		let eTrimStep = '';
		let targetCropOptimization = '';
		const filterTags = (optimizations?.targetFilterTags || [])
			.map(tag => (typeof tag === 'string' ? tag.trim() : ''))
			.filter(tag => !!tag);
		const targetFilterSegment = filterTags.length > 0 ? `,target_filter=${filterTags.join('|')}` : '';
		let transformations = [];

		const hasTargetCrop = LayerOptimizationsDto.hasAnyTargetCrop(optimizations);
		const hasDrawnCrop = LayerOptimizationsDto.hasDrawnTargetCrop(optimizations);
		const hasTargetCropConfig = LayerOptimizationsDto.hasTargetCropConfig(optimizations?.targetCrop);
		const provider = LayerOptimizationsDto.resolveProvider(optimizations, type);

		if (type === LayerType.IMAGE && optimizations?.provider === 'cloudinary' && hasDrawnCrop) {
			console.warn(
				'[crop] layer pins the cloudinary provider but carries a drawn crop; using tasker, ' +
					'which is the only provider that implements c_target'
			);
		}

		// If we don't have a target crop, optimizations, or resize mode, return the assetPath
		// if (!hasTargetCrop && !qualityOptimization && !resizeConfig.mode) {
		// 	return assetPath;
		// }

		// If target crop is enabled, ignore gravity as it conflicts with tasker
		// Also default resize mode to 'fill' if not set
		if (hasTargetCrop) {
			resizeConfig = {
				...resizeConfig,
				gravity: resizeConfig?.gravity ? undefined : resizeConfig?.gravity,
				mode: resizeConfig?.mode || ResizeMode.FILL
			};
		}

		// Tasker trims in its own step before the resize, so the resize meets the content's own ratio
		// rather than the asset's, and a fill to any other ratio cuts content nobody chose to lose
		// (#11110). Only a crop that reaches the URL fixes the ratio first: a drawn one, or a configured
		// one whose auto flag is not off (the emit branches below). A leftover object - the flag switched
		// off with nothing drawn, a partial rect, bare width/height - still makes the provider tasker and
		// defaults the mode above but emits nothing, so it must not block the fit. Read after that default.
		// Cloudinary trims inside the resize step and keeps its URL.
		const emitsTargetCrop = hasTargetCropConfig && (hasDrawnCrop || optimizations.targetCrop.autoTargetCropEnabled !== false);
		const trimReplacesFill =
			type === LayerType.IMAGE &&
			provider === 'tasker' &&
			!!eTrimOptimization?.enabled &&
			!emitsTargetCrop &&
			(resizeConfig?.mode === ResizeMode.FILL || resizeConfig?.mode === ResizeMode.LFILL);
		// A trim URL always carries force_cloudinary, and Cloudinary answers 500 for g_auto and
		// g_attention next to c_fit. A fit has nothing to position anyway. Also skips the xy_center
		// x_/y_ tokens below.
		if (trimReplacesFill) {
			resizeConfig = { ...resizeConfig, gravity: undefined };
		}

		// Check if we'll be using c_subpos (will be determined later when processing target crop)
		const hasSubjectPosition = hasValidSubjectPosition(optimizations?.subjectPosition);
		const willUseSubjectPositioning = hasSubjectPosition && validateSubjectPositionBounds(optimizations.subjectPosition).length === 0;

		// Apply transformations. These are same for video and image
		// Skip resize transformations if using c_subpos (they're included in c_subpos itself)
		if (resizeConfig?.mode && !willUseSubjectPositioning) {
			// If either width or height is more than 2000px, let's resize the largest side to 2000px
			// and keep the aspect ratio.
			let width = Math.ceil(Number(resizeConfig.width || 0));
			let height = Math.ceil(Number(resizeConfig.height || 0));

			if (resizeConfig.maxResolution) {
				let maxResolution = Number(resizeConfig.maxResolution);

				// Determine the scale factor needed to resize the larger dimension to maxResolution
				let scale = Math.min(maxResolution / width, maxResolution / height);

				// Apply the scale to both dimensions, but only if scale is less than 1 (i.e., a reduction is needed)
				if (scale < 1) {
					width = Math.ceil(width * scale);
					height = Math.ceil(height * scale);
				}
			}

			if (width) {
				// Make sure there are no decimals in the width.  Let's round up always.
				transformations.push(`w_${width}`);
			} else {
				// If width is not provided, use element width
				transformations.push(`w_$element.width`);
			}

			if (height) {
				// Make sure there are no decimals in the height.  Let's round up always.
				transformations.push(`h_${height}`);
			} else {
				// If width is not provided, use element width
				transformations.push(`h_$element.height`);
			}

			// c_target has already fixed the ratio, and a Tasker trim replaces it with the content's own:
			// a fill to author dims of another ratio would shave the region the user framed or the product
			// the trim kept. Fit keeps the pixel budget without cropping.
			const cropping = resizeConfig.mode === ResizeMode.FILL || resizeConfig.mode === ResizeMode.LFILL;
			const resizeMode =
				cropping && ((type === LayerType.IMAGE && hasDrawnCrop) || trimReplacesFill)
					? resizeConfig.mode === ResizeMode.FILL
						? ResizeMode.FIT
						: ResizeMode.LIMIT
					: resizeConfig.mode;
			transformations.push(`c_${resizeMode}`);

			if (resizeConfig?.gravity) {
				transformations.push(`g_${resizeConfig.gravity}`);
			}

			if (provider === 'tasker') {
				transformations.push(`fl_progressive`);

				// Auto-convert format for optimal delivery (e.g., tiff → jpeg/webp)
				if (type === 'image') {
					transformations.push(`f_auto`);
				}

				// we_ operations are only available within FIT or FILL resize modes
				if (resizeConfig?.mode === ResizeMode.FIT || resizeConfig?.mode === ResizeMode.FILL) {
					// Width enforcement controls whether Tasker can upscale images beyond their original size
					// we_true = prevent upscaling (default), we_false = allow upscaling
					if (optimizations?.disableWidthEnforcement) {
						transformations.push(`we_false`);
					} else {
						transformations.push(`we_true`);
					}
				}
			}
		}

		if (type === 'video') {
			if (optimizations.videoTrimming) {
				const { startOffset, endOffset, duration } = optimizations.videoTrimming;
				if (startOffset) {
					transformations.push(`so_${startOffset}`);
				}
				if (endOffset) {
					transformations.push(`eo_${endOffset}`);
				}
				if (duration) {
					transformations.push(`du_${duration}`);
				}
			}
		}

		if (type === 'image') {
			if (resizeConfig?.gravity === 'xy_center' && resizeConfig.x !== undefined && resizeConfig.y !== undefined) {
				transformations.push(`x_${resizeConfig.x},y_${resizeConfig.y}`);
			}

			if (resizeConfig?.zoom) {
				// Ignore if zoom isn't a number.
				if (isNaN(Number(resizeConfig.zoom))) {
					console.warn('Zoom level must be a number.');
				} else if (Number(resizeConfig.zoom) <= 100) {
					console.warn('Zoom level must be greater than 100.');
				} else {
					let zoom = Number(resizeConfig.zoom) / 100;
					const cropSize = 1 / zoom; // The width and height of the cropped area
					const offset = 1 - cropSize;

					switch (resizeConfig.zoomGravity) {
						case ZoomGravity.NORTH:
							zoomOptimization = `x_${(offset / 2).toFixed(4)},y_0,w_${cropSize.toFixed(4)},h_${cropSize.toFixed(4)},c_crop/`;
							break;
						case ZoomGravity.SOUTH:
							zoomOptimization = `x_${(offset / 2).toFixed(4)},y_${offset.toFixed(4)},w_${cropSize.toFixed(
								4
							)},h_${cropSize.toFixed(4)},c_crop/`;
							break;
						case ZoomGravity.EAST:
							zoomOptimization = `x_${offset.toFixed(4)},y_${(offset / 2).toFixed(4)},w_${cropSize.toFixed(
								4
							)},h_${cropSize.toFixed(4)},c_crop/`;
							break;
						case ZoomGravity.WEST:
							zoomOptimization = `x_0,y_${(offset / 2).toFixed(4)},w_${cropSize.toFixed(4)},h_${cropSize.toFixed(4)},c_crop/`;
							break;
						case ZoomGravity.NORTH_EAST:
							zoomOptimization = `x_${offset.toFixed(4)},y_0,w_${cropSize.toFixed(4)},h_${cropSize.toFixed(4)},c_crop/`;
							break;
						case ZoomGravity.NORTH_WEST:
							zoomOptimization = `x_0,y_0,w_${cropSize.toFixed(4)},h_${cropSize.toFixed(4)},c_crop/`;
							break;
						case ZoomGravity.SOUTH_EAST:
							zoomOptimization = `x_${offset.toFixed(4)},y_${offset.toFixed(4)},w_${cropSize.toFixed(4)},h_${cropSize.toFixed(
								4
							)},c_crop/`;
							break;
						case ZoomGravity.SOUTH_WEST:
							zoomOptimization = `x_0,y_${offset.toFixed(4)},w_${cropSize.toFixed(4)},h_${cropSize.toFixed(4)},c_crop/`;
							break;
						case ZoomGravity.CENTER:
						default:
							const centeredOffset = offset / 2;
							zoomOptimization = `x_${centeredOffset.toFixed(4)},y_${centeredOffset.toFixed(4)},w_${cropSize.toFixed(
								4
							)},h_${cropSize.toFixed(4)},c_crop/`;
							break;
					}
				}
			}

			if (eTrimOptimization?.enabled) {
				let eTrim: string = 'e_trim';

				const trimTolerance = Number(eTrimOptimization.tolerance);
				if (trimTolerance >= 0 && trimTolerance <= 100 && !String.isEmpty(eTrimOptimization.tolerance)) {
					eTrim += `:${trimTolerance}`;
				}

				eTrimStep = eTrim;
			}
		}

		if (type === 'image' && hasTargetCropConfig) {
			let targetCrop = optimizations.targetCrop;

			// Check if auto target crop is enabled (defaults to true if undefined and no manual values exist)
			const isAutoMode = targetCrop.autoTargetCropEnabled !== false;

			// Check if subject position is available and valid
			const hasSubjectPosition = hasValidSubjectPosition(optimizations?.subjectPosition);
			let useSubjectPositioning = false;

			if (hasSubjectPosition) {
				// Validate subject position bounds
				const validationErrors = validateSubjectPositionBounds(optimizations.subjectPosition);

				if (validationErrors.length > 0) {
					console.warn('[SUBJECT POSITION] Validation failed, falling back to c_target:', validationErrors);
				} else {
					useSubjectPositioning = true;
				}
			}

			// Use c_subpos if subject position is valid, otherwise fall back to c_target
			if (useSubjectPositioning) {
				// Build c_subpos transformation
				targetCropOptimization = this.buildSubposTransformation(
					optimizations.subjectPosition,
					targetCrop,
					resizeConfig,
					isAutoMode,
					hasDrawnCrop,
					targetFilterSegment
				);
				targetCropOptimization += '/';
			} else {
				// Fall back to existing c_target logic

				if (isAutoMode && !hasDrawnCrop) {
					// Auto mode: use target_auto to let the proxy select the best focal area
					targetCropOptimization = `c_target,target_auto${targetFilterSegment}`;

					// Turn width and height into an aspect ratio for focal area selection
					const autoRatio = this.reduceAspectRatio(targetCrop.width, targetCrop.height);
					targetCropOptimization += autoRatio ? `,ar_${autoRatio}` : ',ar_$element.ar';

					if (targetCrop?.margin) {
						targetCropOptimization += `,${targetCrop.margin}`;
					}

					// Add aspect priority parameter (ap_) - defaults to 'target' if not specified
					if (targetCrop.aspectPriority) {
						targetCropOptimization += `,ap_${targetCrop.aspectPriority}`;
					}

					// Set the mode to be cover if its not already in there.
					if (targetCrop.cropMode === 'contain') {
						targetCropOptimization += ',am_contain';
					} else {
						targetCropOptimization += ',am_cover';
					}
				} else if (hasDrawnCrop) {
					// Manual mode: use specific tx/ty/tw/th coordinates
					targetCropOptimization = `c_target`;
					targetCropOptimization +=
						`,tx_${LayerOptimizationsDto.asCropToken(targetCrop.tx)},ty_${LayerOptimizationsDto.asCropToken(targetCrop.ty)}` +
						`,tw_${LayerOptimizationsDto.asCropToken(targetCrop.tw)},th_${LayerOptimizationsDto.asCropToken(targetCrop.th)}`;

					// The renderer resolves this from the rendered frame and falls back to
					// targetCrop.width/height.
					targetCropOptimization += ',ar_$element.ar';

					if (targetCrop?.margin) {
						targetCropOptimization += `,${targetCrop.margin}`;
					}

					// Add aspect priority parameter (ap_) - defaults to 'target' if not specified
					if (targetCrop.aspectPriority) {
						targetCropOptimization += `,ap_${targetCrop.aspectPriority}`;
					}

					// Set the mode to be cover if its not already in there.
					if (targetCrop.cropMode === 'contain') {
						targetCropOptimization += ',am_contain';
					} else if (targetCropOptimization.indexOf('am_cover') === -1) {
						targetCropOptimization += ',am_cover';
					}
				}

				targetCropOptimization += '/';
				// console.log('Target crop optimization', targetCropOptimization);
			}
		}

		// If there is an advanced string, remove everything else and use it.
		if (optimizations?.advanced) {
			// Remove any starting and ending slashes.
			let advanced = optimizations.advanced.replace(/^\/|\/$/g, '');
			transformations = [advanced];
			eTrimStep = '';
		}

		// Apply cloudinary optimizations to video and image type layers
		if (type === 'video' || type === 'image') {
			if (qualityOptimization || transformations.length > 0 || targetCropOptimization || eTrimStep) {
				let finalUrl = assetPath;

				if (!assetPath) {
					console.error('Asset path is required for optimization.', assetPath);
					return assetPath;
				}

				if (provider === 'tasker') {
					if (!taskerEngineUrl) {
						console.error('A taskerEngineUrl environment variable is required for tasker optimization.', assetPath);
						return assetPath;
					}

					// Check if using c_subpos or c_target with auto mode
					const targetCrop = optimizations?.targetCrop;
					const isAutoMode = targetCrop?.autoTargetCropEnabled !== false;
					const hasAutoTargetCrop = isAutoMode && !hasDrawnCrop && targetCropOptimization.includes('target_auto');
					const isC_Subpos = targetCropOptimization.includes('c_subpos');
					const hasAutoC_Subpos = isC_Subpos && hasAutoTargetCrop;
					// The trim step, then everything else in one resize step.
					const resizeStep = [transformations.join(','), qualityOptimization].filter(Boolean).join(',');
					const steps = [eTrimStep, resizeStep].filter(Boolean);
					// Tasker reads force_cloudinary once, on the last step: repeated on the
					// trim step as well, it skipped the resize.
					const taskerForceFallback = qualityOptimization.includes('auto') || !!eTrimStep || resizeStep.includes('e_trim');
					if (taskerForceFallback && steps.length > 0) {
						steps[steps.length - 1] += ',force_cloudinary';
					}

					/*
					console.log('[AUTO CROP DEBUG]', {
						targetCrop,
						autoTargetCropEnabled: targetCrop?.autoTargetCropEnabled,
						isAutoMode,
						hasDrawnCrop,
						targetCropOptimization,
						hasAutoTargetCrop,
						isC_Subpos,
						hasAutoC_Subpos
					});
					*/

					const urlParameters = `${targetCropOptimization}${zoomOptimization}${steps.join('/')}`;

					if (hasAutoTargetCrop) {
						// Route through API process endpoint for auto focal area detection
						// This handles both c_target,target_auto and c_subpos,target_auto
						// The processor will load focal areas from DB, select the best match, and forward to Tasker
						const apiBaseUrl = apiUrl || process.env.API_BASE_URL || 'http://localhost:8001';
						finalUrl = `${apiBaseUrl}/asset/process/${urlParameters}/path/${assetPath}`;

						// Add cache-busting parameter if seed is provided
						if (seed) {
							finalUrl = CacheBusterUtils.appendCacheBusterToUrl(finalUrl, seed);
						}
					} else {
						// Direct to Tasker for manual target crop/c_subpos or no target crop
						finalUrl = `${taskerEngineUrl.replace(/\/$/, '')}/asset/${urlParameters}/path/${assetPath}`;
						// console.log('Final URL', finalUrl);
					}
				} else {
					// Use Cloudinary

					if (!bucketId) {
						console.error('Bucket ID is required for cloudinary optimization.', assetPath);
						return assetPath;
					}

					// Combined in one step, Cloudinary applies the resize first and then trims the
					// already-scaled canvas, so the image lands at the wrong final dimensions. Emitting
					// the trim as its own step before the resize fixes that, but it also changes the URL
					// (and therefore the rendered output) for every template authored against the legacy
					// order. So it is opt-in per layer via eTrim.trimFirst; the default keeps the trim in
					// the resize step, exactly as before. Quality stays on the resize step either way.
					let joinedSteps: string;
					if (eTrimOptimization?.trimFirst && eTrimStep) {
						const resizeStep = [transformations.join(','), qualityOptimization].filter(Boolean).join(',');
						joinedSteps = [eTrimStep, resizeStep].filter(Boolean).join('/');
					} else {
						joinedSteps = [...transformations, eTrimStep, qualityOptimization].filter(Boolean).join(',');
					}

					// Recognize if the asset path is a cloudinary path.  If it is, don't use the fetch url.
					finalUrl = `https://res.cloudinary.com/${bucketId}/${type}/fetch/${targetCropOptimization}${zoomOptimization}${joinedSteps}/${assetPath}`;

					// Don't use fetch if the asset path is already a cloudinary path.
					if (assetPath && assetPath.includes('res.cloudinary.com')) {
						// Insert the transformations right after the 'upload' part of the image path.
						const parts = assetPath.split('/');
						const uploadIndex = parts.findIndex(part => part === 'upload');

						let clTransformations = `${targetCropOptimization}${zoomOptimization}${joinedSteps}`;

						// Detect if we have a baseline optimization and add it first.
						if (optimizations.baseline) {
							clTransformations = `bl_${optimizations.baseline}/${clTransformations}`;
						}

						parts.splice(uploadIndex + 1, 0, clTransformations);

						finalUrl = parts.join('/');
					}
				}

				// Replace any double slashes with a single slash, but not http:// or https://
				finalUrl = finalUrl.replace(/([^:]\/)\/+/g, '$1');

				return finalUrl;
			}
		}

		return assetPath;
	}

	/**
	 * Preview quality for tasker-served images. Deliberately not a `q_auto` value: the fallback
	 * rule in optimizeAsset appends force_cloudinary for any auto quality, which routes the whole
	 * job through a second provider and costs several seconds on a cold derivative.
	 */
	public static readonly TASKER_PREVIEW_QUALITY = 'q_70';

	public static readonly NON_RASTER_IMAGE_OPTIMIZATION_KEYS: readonly string[] = [
		'targetCrop',
		'resize',
		'provider',
		'subjectPosition',
		'targetFilterTags',
		'disableWidthEnforcement'
	];

	private static readonly RASTER_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
	private static readonly VIDEO_EXTENSIONS = ['mp4', 'mov', 'm4v', 'webm'];

	private static getAssetExtension(assetPath: string): string | undefined {
		if (!assetPath) {
			return undefined;
		}

		const pathWithoutParams = assetPath.split('?')?.[0];
		const extension = pathWithoutParams?.split('.')?.pop()?.toLowerCase();
		return extension || undefined;
	}

	//removed resolution and mode from resize config for non-raster assets
	private static sanitizeResizeForNonRaster(resize?: LayerOptimizationResizeDto): LayerOptimizationResizeDto | undefined {
		if (!resize) {
			return undefined;
		}

		const sanitized: Partial<LayerOptimizationResizeDto> = {};

		if (resize.mode) {
			sanitized.mode = resize.mode;
		}
		if (resize.width) {
			sanitized.width = resize.width;
		}
		if (resize.height) {
			sanitized.height = resize.height;
		}

		return Object.keys(sanitized).length ? (sanitized as LayerOptimizationResizeDto) : undefined;
	}

	/**
	 * Which optimization keys may be kept for this asset.
	 */
	public static getSupportedOptimizations(assetPath: string, assetType: LayerType) {
		const extension = this.getAssetExtension(assetPath);

		switch (assetType) {
			case LayerType.IMAGE:
				if (extension && this.RASTER_IMAGE_EXTENSIONS.includes(extension)) {
					return 'all';
				}
				return this.NON_RASTER_IMAGE_OPTIMIZATION_KEYS;
			case LayerType.VIDEO:
				return 'all';
			default:
				return 'all';
		}
	}

	public static filterOptimizationsForAsset(
		optimizations: LayerOptimizationsDto,
		supportedKeys: readonly string[]
	): LayerOptimizationsDto | undefined {
		if (!optimizations) {
			return undefined;
		}

		const filtered: Partial<LayerOptimizationsDto> = {};

		for (const key of supportedKeys) {
			const value = optimizations[key as keyof LayerOptimizationsDto];
			if (value === undefined || value === null) {
				continue;
			}

			if (key === 'targetCrop') {
				if (this.hasTargetCropConfig(value as LayerOptimizationTargetCropDto)) {
					filtered.targetCrop = value as LayerOptimizationTargetCropDto;
				}
				continue;
			}

			if (key === 'resize') {
				const resize = this.sanitizeResizeForNonRaster(value as LayerOptimizationResizeDto);
				if (resize) {
					filtered.resize = resize;
				}
				continue;
			}

			if (key === 'targetFilterTags') {
				const tags = value as string[];
				if (tags.length) {
					filtered.targetFilterTags = tags;
				}
				continue;
			}

			if (key === 'provider') {
				filtered.provider = optimizations.provider;
				continue;
			}

			if (key === 'subjectPosition') {
				filtered.subjectPosition = optimizations.subjectPosition;
				continue;
			}

			if (key === 'disableWidthEnforcement') {
				filtered.disableWidthEnforcement = optimizations.disableWidthEnforcement;
			}
		}

		return Object.keys(filtered).length ? (filtered as LayerOptimizationsDto) : undefined;
	}

	public static supportsPreviewEnrichment(assetPath: string, assetType: LayerType): boolean {
		const extension = this.getAssetExtension(assetPath);
		if (!extension) {
			return false;
		}

		switch (assetType) {
			case LayerType.IMAGE:
				return this.RASTER_IMAGE_EXTENSIONS.includes(extension);
			case LayerType.VIDEO:
				return this.VIDEO_EXTENSIONS.includes(extension);
			default:
				return false;
		}
	}
}
